<template>
  <div class="agent-composer">
    <drop-overlay v-if="!readOnly" size="small" @upload-files="onFiles">Drop files for the agent</drop-overlay>

    <!-- The page the message was sent from, the way the chat composer links one. The agent is
         told the label so "this article" has something to mean. -->
    <div v-if="!readOnly" class="agent-composer__link-row">
      <template v-if="context">
        <span class="agent-composer__link">
          <span class="agent-composer__link-icon" v-html="linkIcon" />
          <span class="agent-composer__link-label">{{ context.label }}</span>
          <button
            type="button"
            class="agent-composer__link-remove"
            title="Don't mention this page to the agent"
            @click="dismissed = true"
          >
            <span v-html="crossIcon" />
          </button>
        </span>
        <span class="agent-composer__link-note">Auto-linked</span>
      </template>
      <template v-else-if="detected">
        <button type="button" class="agent-composer__link agent-composer__link--empty" @click="dismissed = false">
          <span class="agent-composer__link-icon" v-html="linkIcon" />
          <span class="agent-composer__link-label">Mention {{ detected.label }}</span>
        </button>
      </template>
    </div>

    <div v-if="attachments.length" class="agent-composer__attachments">
      <span v-for="attachment in attachments" :key="attachment.key" class="agent-composer__chip">
        <file-icon :file-name="attachment.name" class="agent-composer__chip-icon" />
        <span class="agent-composer__chip-name">{{ attachment.name }}</span>
        <span v-if="attachment.progress < 100" class="agent-composer__chip-progress">{{ attachment.progress }}%</span>
        <button type="button" class="agent-composer__chip-remove" title="Remove" @click="remove(attachment.key)">
          <span v-html="crossIcon" />
        </button>
      </span>
    </div>

    <div class="agent-composer__input-wrap" :class="{ 'agent-composer__input-wrap--filled': canSend }">
      <textarea-custom
        ref="textareaRef"
        v-model="text"
        class="agent-composer__textarea"
        :placeholder="placeholder"
        :autosize="true"
        :min-height="62"
        :max-height="220"
        :max-symbols="false"
        :is-disabled="readOnly"
        @keydown="onKeydown"
      />

      <div class="agent-composer__actions">
        <button
          type="button"
          class="agent-composer__icon-btn"
          title="Attach a file"
          :disabled="readOnly"
          @click="pickFiles"
        >
          <span class="agent-composer__btn-icon" v-html="attachIcon" />
        </button>
        <input ref="fileInputRef" type="file" multiple class="agent-composer__file-input" @change="onFilePicked" />

        <span class="agent-composer__hint">{{ hint }}</span>

        <div class="agent-composer__actions-right">
          <fs-button
            size="small"
            accent
            :disabled="!canSend"
            :loading="sending"
            :title="busy ? 'Queue this for when the agent finishes (Enter)' : 'Send (Enter)'"
            @click="submit"
          >
            <template #icon><span class="agent-composer__btn-icon" v-html="sendIcon" /></template>
            {{ busy ? 'Queue' : 'Send' }}
          </fs-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAgentChat } from '@/store/agent-chat';
import { useAzureBlobFiles } from '@/store/azure-blob-files';
import { useGeneral } from '@/store/general';
import { useChatContext, type ChatContext } from '@lib/chat-context';
import { usePasteUpload } from '@lib/paste-upload';
import DropOverlay from '@components/files/drop-overlay.vue';
import FileIcon from '@components/files/file-icon.vue';
import crossIcon from '@icons/other/ico-cross.svg';
import linkIcon from '@icons/other2/link-chain.svg';
import sendIcon from '@icons/other2/send.svg';
import attachIcon from '@icons/other2/attachment.svg';
import FsButton from '@components/buttons/fs-button.vue';
import TextareaCustom from '@components/form-elements/textarea-custom.vue';

// `busy` is the agent still working — the composer stays open and the message is queued.
// `readOnly` is an archived thread, which takes nothing more.
const {
  threadId,
  busy = false,
  readOnly = false,
} = defineProps<{
  threadId: number;
  busy?: boolean;
  readOnly?: boolean;
}>();
const emit = defineEmits<{ sent: [] }>();

const text = ref('');
const sending = ref(false);
const textareaRef = ref<{ focus?: () => void }>();
const fileInputRef = ref<HTMLInputElement>();

const placeholder = computed(() => {
  if (readOnly) return 'This thread is archived.';
  if (busy) return 'Add to what the agent is working on — it reads this when the turn ends';
  return 'Ask the agent, or tell it what to do in the PLM';
});

const hint = computed(() => {
  if (readOnly) return 'Archived threads keep their transcript but take no more messages.';
  return busy ? 'Queued until the agent finishes this turn' : 'Enter to send · Shift+Enter for a new line';
});

// The page this message is being written on, and whether the user waved it off. Same shape as
// the chat composer's chip: navigating is new intent, so the offer comes back.
const getContext = useChatContext();
const detected = ref<ChatContext | null>(getContext());
const dismissed = ref(false);
const context = computed<ChatContext | null>(() => (dismissed.value ? null : detected.value));

const redetect = () => {
  detected.value = getContext();
  dismissed.value = false;
};
watch(useRoute(), redetect);
watch(() => useGeneral().headTitle, redetect);

// Files are uploaded to the brand as they are added, so sending is one request that names
// their ids. A chip with progress under 100 is still on its way up.
type PendingAttachment = { key: number; name: string; progress: number; fileId: number | null };
const attachments = ref<PendingAttachment[]>([]);
let nextKey = 1;

// Reading the entry back out of the ref is what makes the update land: pushing a plain object
// into a reactive array stores the object, but hands back a proxy on read — and only writes
// through that proxy re-render. Holding the pushed reference and mutating it leaves the chip
// sitting at 0% for an upload that has in fact finished.
const patch = (key: number, changes: Partial<PendingAttachment>) => {
  const live = attachments.value.find((a) => a.key === key);
  if (live) Object.assign(live, changes);
};

const onFiles = (files: FileList | File[]) => {
  for (const file of Array.from(files)) {
    const key = nextKey++;
    attachments.value.push({ key, name: file.name, progress: 0, fileId: null });
    useAzureBlobFiles()
      .upload(file, (percentage) => patch(key, { progress: Math.min(percentage, 99) }))
      .then((uploaded) => patch(key, { fileId: uploaded.id, progress: 100 }))
      .catch(() => remove(key));
  }
};
const remove = (key: number) => (attachments.value = attachments.value.filter((a) => a.key !== key));

usePasteUpload(onFiles, () => !readOnly);

const pickFiles = () => fileInputRef.value?.click();
const onFilePicked = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (input.files?.length) onFiles(input.files);
  input.value = '';
};

// The panel unmounts when it closes, so what is half-typed lives in the store rather than here.
// Attachments are already uploaded by this point, so keeping their ids costs nothing and saves
// the user re-dropping the file.
const remember = () =>
  useAgentChat().setDraft(threadId, {
    text: text.value,
    attachments: attachments.value
      .filter((a) => a.fileId !== null)
      .map((a) => ({ key: a.key, name: a.name, fileId: a.fileId! })),
  });

onMounted(() => {
  const draft = useAgentChat().draftFor(threadId);
  text.value = draft.text;
  attachments.value = draft.attachments.map((a) => ({ ...a, progress: 100 }));
  nextKey = Math.max(0, ...draft.attachments.map((a) => a.key)) + 1;
});
onUnmounted(remember);

const uploading = computed(() => attachments.value.some((a) => a.fileId === null));
const canSend = computed(
  () =>
    !readOnly && !sending.value && !uploading.value && (text.value.trim().length > 0 || attachments.value.length > 0)
);

const submit = async () => {
  if (!canSend.value) return;
  sending.value = true;
  try {
    await useAgentChat().send(
      text.value,
      attachments.value.map((a) => a.fileId!),
      context.value ?? undefined
    );
    text.value = '';
    attachments.value = [];
    remember();
    emit('sent');
    redetect();
  } finally {
    sending.value = false;
  }
};

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
    event.preventDefault();
    submit();
  }
};

defineExpose({ focus: () => textareaRef.value?.focus?.() });
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

.agent-composer {
  position: relative;
  padding: 1.4rem 2rem 2rem;
  background: $c-bg-gray-darker-hover;
  border-top: 1px solid rgba($c-ink, 0.07);

  // ── Linked page ──────────────────────────────────────────────────────────
  &__link-row {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    min-height: 2.8rem;
    margin-bottom: 0.6rem;
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    max-width: 26rem;
    height: 2.8rem;
    padding: 0 0.4rem 0 1.1rem;
    box-sizing: border-box;
    border: none;
    border-radius: $br-pill;
    background: $c-accent-soft;
    font-size: 1.2rem;
    color: $c-on-accent-soft;

    &--empty {
      padding: 0 1.1rem;
      border: 1px dashed rgba($c-ink, 0.22);
      background: none;
      color: $c-ink-soft;
      cursor: pointer;
      transition: border-color $transition-fast ease;

      &:hover {
        border-color: rgba($c-ink, 0.5);
      }
    }
  }

  &__link-icon {
    display: flex;
    flex: none;

    :deep(svg) {
      width: 1rem;
      height: 1rem;
      fill: currentColor;
    }
  }

  &__link-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__link-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: none;
    cursor: pointer;

    &:hover {
      background: rgba($c-on-accent-soft, 0.15);
    }

    :deep(svg) {
      width: 0.9rem;
      height: 0.9rem;
      fill: currentColor;
    }
  }

  &__link-note {
    font-size: 1.1rem;
    color: rgba($c-ink, 0.5);
  }

  // ── Attachments ──────────────────────────────────────────────────────────
  &__attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    margin-bottom: 0.8rem;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    max-width: 28rem;
    padding: 0.4rem 0.6rem;
    border: 1px solid $c-hairline-strong;
    border-radius: $br-pill;
    background: $c-surface;
    font-size: 1.2rem;
  }

  &__chip-icon {
    transform: scale(0.8);
  }

  &__chip-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chip-progress {
    color: $c-ink-soft;
  }

  &__chip-remove {
    display: inline-flex;
    padding: 0.2rem;
    border: none;
    background: none;
    cursor: pointer;

    :deep(svg) {
      width: 0.8rem;
      height: 0.8rem;
      fill: rgba($c-ink, 0.5);
    }
  }

  // ── Input ────────────────────────────────────────────────────────────────
  &__input-wrap {
    position: relative;
    padding-bottom: 0.6rem;
    border: 1px solid $c-hairline-strong;
    border-radius: $br-very-heavy;
    background: $c-surface;
    transition: border-color $transition-fast ease;

    &--filled {
      border-color: rgba($c-ink, 0.28);
    }
  }

  // textarea-custom paints its own grey well and darkens it on hover/focus. In the
  // composer the white box around it is the surface, so the control stays transparent.
  &__input-wrap &__textarea {
    background: none;

    &:hover,
    &.custom-textarea--active {
      background: none;
    }

    :deep(textarea) {
      padding: 1.4rem 1.6rem 0.6rem;
      border: none;
      background: none;
      font-size: 1.4rem;
      line-height: 2.1rem;
      resize: none;

      &:focus {
        border: none;
        box-shadow: none;
      }
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0 1rem 0 1.2rem;
  }

  &__icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    padding: 0;
    border: none;
    border-radius: $br-medium-heavy;
    background: none;
    cursor: pointer;
    transition: background $transition-fast ease;

    &:hover:not(:disabled) {
      background: $c-surface-muted;
    }

    &:disabled {
      opacity: 0.4;
      cursor: default;
    }
  }

  &__btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;

    :deep(svg) {
      width: 1.4rem;
      height: 1.4rem;
      fill: currentColor;
    }
  }

  &__file-input {
    display: none;
  }

  &__hint {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 1.1rem;
    color: rgba($c-ink, 0.45);
  }

  &__actions-right {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }
}
</style>
